import * as THREE from "three";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { getEffectiveWorldBodies } from "../stores/sceneActions/bodyUtils";
import { getStandardFloorHField } from "./terrainFloor";
import { sampleMaxHFieldHeightInModelBounds } from "./hfieldModelSampling";
import { logger } from "./logger";

const LIFT_EPS = 0.001;

function isRenderableGeomObject(object) {
  let cur = object;
  while (cur) {
    const type = cur.userData?.type;
    if (type === "mesh" || type === "collision" || type === "visual") return true;
    cur = cur.parent;
  }
  return false;
}

/**
 * For one world-root body: measure lowest renderable mesh point in model space and move the body
 * up/down so that point rests on the current standard terrain heightfield under its XY footprint.
 * Matches `ensureBodyBoundsAboveSupport` when the support is the scene floor / terrain (library drop correction).
 *
 * @param {string} bodyUuid
 * @param {Record<string, import("three").Object3D | undefined>} sceneObjectsMap
 * @param {import("three").Object3D} modelGroup
 * @param {import("../types/scene").HField} floorHField
 * @returns {boolean} Whether a transform was applied.
 */
export function liftWorldRootBodyOntoStandardTerrainHeightfield(
  bodyUuid,
  sceneObjectsMap,
  modelGroup,
  floorHField
) {
  if (!bodyUuid || !modelGroup) return false;

  const bodyObj = sceneObjectsMap?.[bodyUuid];
  const renderBodyObj = sceneObjectsMap?.[`b_${bodyUuid}`];
  const bodyObjects = [bodyObj, renderBodyObj].filter(Boolean);
  if (bodyObjects.length === 0) return false;

  const worldBounds = new THREE.Box3();
  let hasGeomBounds = false;
  bodyObjects.forEach((obj) => {
    obj.updateWorldMatrix(true, true);
    obj.traverse((child) => {
      if (!child?.isMesh || !child.visible || !child.geometry) return;
      if (!isRenderableGeomObject(child)) return;
      const geometry = child.geometry;
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox?.();
      }
      if (!geometry.boundingBox) return;
      const meshBounds = geometry.boundingBox.clone().applyMatrix4(child.matrixWorld);
      if (!hasGeomBounds) {
        worldBounds.copy(meshBounds);
        hasGeomBounds = true;
      } else {
        worldBounds.union(meshBounds);
      }
    });
  });
  if (!hasGeomBounds || worldBounds.isEmpty()) return false;

  const corners = [
    new THREE.Vector3(worldBounds.min.x, worldBounds.min.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.min.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.max.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.max.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.min.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.min.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.max.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.max.y, worldBounds.max.z),
  ];

  let minLocalZ = Infinity;
  const modelBounds = new THREE.Box3();
  corners.forEach((corner) => {
    const localCorner = corner.clone();
    modelGroup.worldToLocal(localCorner);
    if (localCorner.z < minLocalZ) minLocalZ = localCorner.z;
    modelBounds.expandByPoint(localCorner);
  });

  const terrainHeight = sampleMaxHFieldHeightInModelBounds(floorHField, modelBounds);
  const supportZ = terrainHeight != null && Number.isFinite(terrainHeight) ? terrainHeight : null;
  if (supportZ == null) return false;

  if (!Number.isFinite(minLocalZ)) return false;

  /** If the whole footprint sits off the terrain patch, sampling can miss; avoid moving. */
  if (minLocalZ >= supportZ - 1e-7) return true;

  const body = getSceneState().bodies?.[bodyUuid];
  if (!body || !Array.isArray(body.position) || body.position.length !== 3) return false;

  const nextPosition = [
    body.position[0],
    body.position[1],
    body.position[2] + (supportZ - minLocalZ) + LIFT_EPS,
  ];
  const nextOrientation =
    Array.isArray(body.orientation) && body.orientation.length === 4
      ? body.orientation
      : [1, 0, 0, 0];
  getSceneActions().setBodyTransform(bodyUuid, nextPosition, nextOrientation);
  return true;
}

/**
 * Repositions every effective world-root body so bottoms sit on the current standard terrain
 * (same idea as asset-library placement correction after terrain).
 *
 * @param {{ current?: Record<string, import("three").Object3D | undefined> } | null} sceneObjectsRef
 * @param {{ current?: import("three").Object3D | null } | null} modelGroupRef
 */
export function liftWorldRootBodiesOntoStandardTerrain(sceneObjectsRef, modelGroupRef) {
  const map = sceneObjectsRef?.current;
  const modelGroup = modelGroupRef?.current;
  if (!map || !modelGroup) return;

  const floorHField = getStandardFloorHField(getSceneState()?.hfields);
  if (!floorHField) return;

  const state = getSceneState();
  const effectiveWorldBodies = getEffectiveWorldBodies(state);
  const uuidList = effectiveWorldBodies
    .map((wb) => (typeof wb === "string" ? wb : wb?.uuid))
    .filter((id) => typeof id === "string");

  uuidList.forEach((bodyUuid) => {
    try {
      liftWorldRootBodyOntoStandardTerrainHeightfield(bodyUuid, map, modelGroup, floorHField);
    } catch (e) {
      logger.warn("[Terrain] Failed to reposition body onto terrain", bodyUuid, e);
    }
  });
}

const SNAP_DELTA_EPS = 1e-7;

/**
 * Snap every effective world-root body so its lowest renderable point meets a horizontal support plane in
 * model space (MuJoCo Z). Used after standard floor terrain is removed so objects settle back onto the flat floor.
 *
 * Unlike {@link liftWorldRootBodyOntoStandardTerrainHeightfield}, always applies the vertical correction when
 * the bottom is above or below the plane (drops floating bodies as well as lifting subsurface ones).
 *
 * @param {{ current?: Record<string, import("three").Object3D | undefined> } | null} sceneObjectsRef
 * @param {{ current?: import("three").Object3D | null } | null} modelGroupRef
 * @param {number} [supportZModel=0] Model-space Z of the planar floor after terrain is cleared (matches library drop baseline).
 */
export function liftWorldRootBodiesOntoFlatFloorPlane(
  sceneObjectsRef,
  modelGroupRef,
  supportZModel = 0
) {
  const map = sceneObjectsRef?.current;
  const modelGroup = modelGroupRef?.current;
  if (!map || !modelGroup) return;

  const planeZRaw = Number(supportZModel);
  const planeZ = Number.isFinite(planeZRaw) ? planeZRaw : 0;

  const state = getSceneState();
  const effectiveWorldBodies = getEffectiveWorldBodies(state);
  const uuidList = effectiveWorldBodies
    .map((wb) => (typeof wb === "string" ? wb : wb?.uuid))
    .filter((id) => typeof id === "string");

  uuidList.forEach((bodyUuid) => {
    try {
      snapWorldRootBodyBottomToHorizontalPlane(bodyUuid, map, modelGroup, planeZ);
    } catch (e) {
      logger.warn("[Terrain] Failed to snap body onto flat floor plane", bodyUuid, e);
    }
  });
}

function snapWorldRootBodyBottomToHorizontalPlane(
  bodyUuid,
  sceneObjectsMap,
  modelGroup,
  /** @type {number} */ supportZ
) {
  if (!bodyUuid || !modelGroup) return false;

  const bodyObj = sceneObjectsMap?.[bodyUuid];
  const renderBodyObj = sceneObjectsMap?.[`b_${bodyUuid}`];
  const bodyObjects = [bodyObj, renderBodyObj].filter(Boolean);
  if (bodyObjects.length === 0) return false;

  const worldBounds = new THREE.Box3();
  let hasGeomBounds = false;
  bodyObjects.forEach((obj) => {
    obj.updateWorldMatrix(true, true);
    obj.traverse((child) => {
      if (!child?.isMesh || !child.visible || !child.geometry) return;
      if (!isRenderableGeomObject(child)) return;
      const geometry = child.geometry;
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox?.();
      }
      if (!geometry.boundingBox) return;
      const meshBounds = geometry.boundingBox.clone().applyMatrix4(child.matrixWorld);
      if (!hasGeomBounds) {
        worldBounds.copy(meshBounds);
        hasGeomBounds = true;
      } else {
        worldBounds.union(meshBounds);
      }
    });
  });
  if (!hasGeomBounds || worldBounds.isEmpty()) return false;

  const corners = [
    new THREE.Vector3(worldBounds.min.x, worldBounds.min.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.min.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.max.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.max.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.min.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.min.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.max.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.max.y, worldBounds.max.z),
  ];

  let minLocalZ = Infinity;
  corners.forEach((corner) => {
    const localCorner = corner.clone();
    modelGroup.worldToLocal(localCorner);
    if (localCorner.z < minLocalZ) minLocalZ = localCorner.z;
  });

  if (!Number.isFinite(minLocalZ)) return false;

  const deltaZ = supportZ + LIFT_EPS - minLocalZ;
  if (Math.abs(deltaZ) < SNAP_DELTA_EPS) return false;

  const body = getSceneState().bodies?.[bodyUuid];
  if (!body || !Array.isArray(body.position) || body.position.length !== 3) return false;

  const nextPosition = [body.position[0], body.position[1], body.position[2] + deltaZ];
  const nextOrientation =
    Array.isArray(body.orientation) && body.orientation.length === 4
      ? body.orientation
      : [1, 0, 0, 0];
  getSceneActions().setBodyTransform(bodyUuid, nextPosition, nextOrientation);
  return true;
}
